import React, { useEffect, useMemo, useState, useRef } from 'react';
import ClientAppShell from '../components/ClientAppShell';
import ContentLoader from '../components/ContentLoader';
import { usePageWalkthrough } from '../hooks/usePageWalkthrough';
import { useChatLivePoll } from '../hooks/useChatLivePoll';
import { getAssessmentDetails, getSubCapabilityAssessmentData } from '../services/assessment';
import { fetchChat, handleSendMessage, downloadChatFile, getChatCount } from '../services/chatApi';
import { useAuth } from '../auth/AuthContext';
import {
    getThreadMessagesNewestFirst,
    getUnreadActionableMessages,
    hasUnreadForCapability,
    hasUnreadForSubCapability,
    isNotificationUnread,
    markChatThreadViewed,
} from '../utils/chatReadState';
import { showAlert } from '../components/AppAlert/appAlert';
import {
    assessmentUnitKey,
    isCapabilityNavHighlighted,
    isLeafSelectedCapability,
    sameAssessmentUnit,
} from '../utils/assessmentUnitUtils';
import {
    chatApiSubCapabilityId,
    chatMessageStorageKey,
} from '../utils/chatThreadUtils';
import { Modal } from '../components/ui/Modal';
import "../assets/css/chat.css";
import "../assets/css/assessment.css";
import "../styles/smechat.css";

const CHAT_MATURITY_STATES = ['aware', 'reactive', 'proactive', 'managed', 'optimized'] as const;

const chatMatchesAssessmentUnit = (
    chat,
    capabilityId,
    subCapabilityId,
    { smeOnly = false } = {}
) => {
    if (smeOnly && chat.senderName !== "SME") {
        return false;
    }
    return sameAssessmentUnit(
        chat.capabilityId,
        chat.subCapabilityId,
        capabilityId,
        subCapabilityId
    );
};

function formatMaturityLabel(state: string) {
    return state.charAt(0).toUpperCase() + state.slice(1);
}

const Chat = () => {
    const basename = process.env.PUBLIC_URL || '';
    const { user } = useAuth();
    const [userName, setUserName] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [chatHistory, setChatHistory] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showModals, setShowModals] = useState([]);
    const [projectId, setProjectId] = useState(null);
    const [userId, setUserId] = useState(null);
    const [messages, setMessages] = useState({});
    const [filesBySubCapability, setFilesBySubCapability] = useState({});
    const [isDisabled, setIsDisabled] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef(null);
    const activeSubCapabilityRef = useRef(null);
    const [currentState, setCurrentState] = useState('');
    const [futureState, setFutureState] = useState('');
    const [currentStatusDescription, setCurrentStatusDescription] = useState('');
    const [futureStatusDescription, setFutureStatusDescription] = useState('');
    const [showAssessmentModal, setShowAssessmentModal] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [subSelectedCard, setSubSelectedCard] = useState(null);
    const [expandedCapability, setExpandedCapability] = useState(null);
    const [latestMessage, setLatestMessage] = useState(null);
    const [readStateVersion, setReadStateVersion] = useState(0);
    const [selectedSubCapability, setSelectedSubCapability] = useState('all');
    const dropdownRefs = useRef({});
    const messagePanelRef = useRef(null);
    const threadBodyRef = useRef(null);
    const autoOpenTimerRef = useRef(null);
    const userOpenedChatRef = useRef(false);
    const body = document.body;
    const [selectedCapability, setSelectedCapability] = useState('all');
    const [isActive, setIsActive] = useState(false);

    // All capability/sub-capability threads with unread SME messages (not just the latest)
    const unreadThreads = useMemo(
        () => getUnreadActionableMessages(chatHistory, userId, projectId, "client"),
        [chatHistory, userId, projectId, readStateVersion]
    );

    // Live message refresh while this page is open; outside chat keeps the existing ~30s badge poll.
    useChatLivePoll({
        userId,
        projectId,
        viewerRole: 'client',
        setChatHistory,
        setLatestMessage,
    });

    const { walkthrough } = usePageWalkthrough({
        userId,
        parentId: 4,
        targetMap: {
            '[data-nav="chat"]': '[data-nav="chat"]',
            '[data-element="message-input"]': '[data-element="message-input"]',
        },
    });

    useEffect(() => {
        if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = false;
            recognitionRef.current.interimResults = false;
            recognitionRef.current.lang = "en-US";

            recognitionRef.current.onresult = (event) => {
                const speechArray = Array.from(event.results);
                const finalTranscript = speechArray
                    .map((result) => result[0].transcript)
                    .join("");
                
                const activeSubCapabilityId = activeSubCapabilityRef.current;
                if (activeSubCapabilityId) {
                    setMessages((prev) => ({
                        ...prev,
                        [activeSubCapabilityId]: finalTranscript,
                    }));
                }
            };

            recognitionRef.current.onerror = (event) => {
                console.error(`Speech recognition error: ${event.error}`);
                setIsListening(false);
            };
        } else {
            console.error("Speech-to-Text is not supported in this browser.");
        }

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.abort();
            }
        };
    }, []);

    const handleDownloadChat = () => {
        if (!chatHistory || chatHistory.length === 0) {
            showAlert('There is no chat history to download yet.', 'info');
            return;
        }
    
        if (!data?.data?.selectedCapabilities || data.data.selectedCapabilities.length === 0) {
            console.error('Capabilities data is empty or undefined:', data?.data?.selectedCapabilities);
            showAlert('Capabilities data is unavailable right now.', 'error');
            return;
        }
    
        let filteredChats;
    
        if (selectedCapability === 'all') {
            const groupedChats = data.data.selectedCapabilities.map((capability) => {
                const capabilityChats = capability.selectedSubCapabilities.map((subCapability) => {
                    const subChats = chatHistory.filter((chat) =>
                        chatMatchesAssessmentUnit(
                            chat,
                            capability.capabilityId,
                            subCapability.subCapabilityId
                        )
                    );
    
                    if (subChats.length > 0) {
                        return [
                            `Sub-Capability: ${subCapability.subCapability}`,
                            ...subChats.map(
                                (chat) =>
                                    `[${chat.senderName}] (${formatChatDate(
                                        new Date(chat.messageDate)
                                    )}): ${chat.message}`
                            ),
                            '',
                        ].join('\n');
                    }
    
                    return '';
                });
    
                return [
                    `Capability: ${capability.capability}`,
                    ...capabilityChats.filter(Boolean),
                    '',
                ].join('\n');
            });
    
            filteredChats = groupedChats.filter(Boolean).join('\n');
        } else {
            const capability = data.data.selectedCapabilities.find(
                (cap) => `${cap.capabilityId}` === `${selectedCapability}`
            );
    
            const capabilityName = capability?.capability || 'Unknown Capability';
    
            if (selectedSubCapability === 'all') {
                const groupedSubChats = capability.selectedSubCapabilities.map((subCapability) => {
                    const subChats = chatHistory.filter((chat) =>
                        chatMatchesAssessmentUnit(
                            chat,
                            selectedCapability,
                            subCapability.subCapabilityId
                        )
                    );
    
                    if (subChats.length > 0) {
                        return [
                            `Sub-Capability: ${subCapability.subCapability}`,
                            ...subChats.map(
                                (chat) =>
                                    `[${chat.senderName}] (${formatChatDate(
                                        new Date(chat.messageDate)
                                    )}): ${chat.message}`
                            ),
                            '',
                        ].join('\n');
                    }
    
                    return '';
                });
    
                filteredChats = [
                    `Capability: ${capabilityName}`,
                    ...groupedSubChats.filter(Boolean),
                ].join('\n');
            } else {
                const subCapabilityName =
                    capability?.selectedSubCapabilities?.find((subCap) =>
                        sameAssessmentUnit(
                            capability.capabilityId,
                            subCap.subCapabilityId,
                            selectedCapability,
                            selectedSubCapability
                        )
                    )?.subCapability || 'Unknown Sub-Capability';
    
                filteredChats = [
                    `Capability: ${capabilityName}`,
                    `Sub-Capability: ${subCapabilityName}`,
                    '',
                    ...chatHistory
                        .filter((chat) =>
                            chatMatchesAssessmentUnit(
                                chat,
                                selectedCapability,
                                selectedSubCapability
                            )
                        )
                        .map(
                            (chat) =>
                                `[${chat.senderName}] (${formatChatDate(
                                    new Date(chat.messageDate)
                                )}): ${chat.message}`
                        ),
                ].join('\n\n');
            }
        }
    
        if (!filteredChats) {
            showAlert('No chats are available for the selected filter.', 'info');
            return;
        }
    
        const blob = new Blob([filteredChats], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
    
        const a = document.createElement('a');
        a.href = url;
        a.download = 'chat-history.txt';
        a.click();
    
        URL.revokeObjectURL(url);
    };        
    
    const getFilteredCapabilities = () => {
        if (!data || !data.data || !data.data.selectedCapabilities) return [];
    
        return data.data.selectedCapabilities.filter((capability) =>
            capability.selectedSubCapabilities.some((subCapability) =>
                chatHistory.some((chat) =>
                    chatMatchesAssessmentUnit(
                        chat,
                        capability.capabilityId,
                        subCapability.subCapabilityId
                    )
                )
            )
        );
    };
    
    const getFilteredSubCapabilities = () => {
        if (selectedCapability === 'all') return [];
    
        const capability = data?.data?.selectedCapabilities.find(
            (cap) => `${cap.capabilityId}` === `${selectedCapability}`
        );
    
        if (!capability) return [];
    
        return capability.selectedSubCapabilities.filter((subCapability) =>
            chatHistory.some((chat) =>
                chatMatchesAssessmentUnit(
                    chat,
                    selectedCapability,
                    subCapability.subCapabilityId
                )
            )
        );
    };     

    const toggleSpeechRecognition = (subCapabilityId) => {
        if (!recognitionRef.current) {
            console.error("Speech Recognition is not initialized.");
            return;
        }

        if (isListening === subCapabilityId) {
            recognitionRef.current.stop();
            setIsListening(false);
            activeSubCapabilityRef.current = null;
        } else {
            if (recognitionRef.current && isListening) {
                recognitionRef.current.stop();
            }
            recognitionRef.current.start();
            setIsListening(subCapabilityId);
            activeSubCapabilityRef.current = subCapabilityId;
        }
    };

    const fetchAssessmentData = async (modal) => {
        try {
            const payload = {
                userId: userId,
                projectId: projectId,
              };

            const assessmentData = await getSubCapabilityAssessmentData(payload);
            const matchedSubCapability = assessmentData.data.find((item) =>
                sameAssessmentUnit(
                    item.capabilityId,
                    item.subCapabilityId,
                    modal.capabilityId,
                    modal.subCapabilityId
                )
            );

            if (matchedSubCapability) {
                setCurrentStatusDescription(matchedSubCapability.currentStatusDescription);
                setFutureStatusDescription(matchedSubCapability.futureStatusDescription);
                setCurrentState(["aware", "reactive", "proactive", "managed", "optimized"][matchedSubCapability.currentStatusRating - 1]);
                setFutureState(["aware", "reactive", "proactive", "managed", "optimized"][matchedSubCapability.futureStatusRating - 1]);
            } else {
                setCurrentStatusDescription('');
                setFutureStatusDescription('');
                setCurrentState('');
                setFutureState('');
            }
        } catch (error) {
            console.error('Error fetching assessment data:', error.message);
        }
    };

    const fetchChatHistory = async () => {
  try {
    
    const payload = { userId, projectId };
    const chatData = await fetchChat(payload);
    const capabilitiesData = await getAssessmentDetails(payload);
    const chatCountData = await getChatCount(payload);

    const updatedCapabilities = capabilitiesData.data.selectedCapabilities.map((capability) => ({
      ...capability,

      selectedSubCapabilities: capability.selectedSubCapabilities.map((subCapability) => {
        //debugger;
        const matchingChatCount = chatCountData.data.chatCountDto.find((chat) =>
            sameAssessmentUnit(
                chat.capabilityId,
                chat.subCapabilityId,
                capability.capabilityId,
                subCapability.subCapabilityId
            )
        );
        return {
          ...subCapability,
          messageCount: matchingChatCount ? matchingChatCount.messageCount : 0,
        };
      }),
    }));

    setData({ ...capabilitiesData, data: { selectedCapabilities: updatedCapabilities } });
    setChatHistory(chatData);

    // Highlight unread SME threads; auto-open uses the most recent unread one
    const unreadActionable = getUnreadActionableMessages(
        chatData,
        userId,
        projectId,
        "client"
    );

    if (unreadActionable.length > 0) {
        const latestUnread = unreadActionable[0];
        setLatestMessage({
            capabilityId: latestUnread.capabilityId,
            subCapabilityId: latestUnread.subCapabilityId,
            messageDate: latestUnread.messageDate,
        });
        // Only jump sidebar selection on first load — not after send/poll while user is in a thread
        if (!userOpenedChatRef.current) {
            setExpandedCapability(latestUnread.capabilityId);
            setSelectedCard(latestUnread.capabilityId);
            setSubSelectedCard(chatApiSubCapabilityId(latestUnread.subCapabilityId));
        }
    } else {
        setLatestMessage(null);
    }
  } catch (error) {
    console.error("Error fetching chat history:", error.message);
  } finally {
    setLoading(false);
  }
};
            
useEffect(() => {
    const storedProjectId = localStorage.getItem('projectId');        
    if (storedProjectId) {
        setProjectId(parseInt(storedProjectId));
    } else if (user) {
        setLoading(false);
    }

    if (user) {
        const storedUserName = user.userName || "Client";
        setUserName(storedUserName);
        setUserId(user.userId);
    } else {
        setUserName("Client");
    }
}, [user]);

    useEffect(() => {
        if (projectId) {
            userOpenedChatRef.current = false;
            fetchAssessmentData();
            fetchChatHistory();
        }
    }, [projectId]);

    useEffect(() => {
        if (selectedCard) {
            const capabilityElement = document.getElementById(`capability-${selectedCard}`);
            capabilityElement?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    
        if (subSelectedCard != null) {
            const subCapabilityElement = document.getElementById(`sub-capability-${subSelectedCard}`);
            subCapabilityElement?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [selectedCard, subSelectedCard]);

    // On mobile/small screens, bring the chat panel into view right after a thread is opened
    useEffect(() => {
        if (!showModal || showModals.length === 0) {
            return;
        }
        if (typeof window === "undefined" || !window.matchMedia("(max-width: 991.98px)").matches) {
            return;
        }
        const rafId = window.requestAnimationFrame(() => {
            messagePanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
        return () => window.cancelAnimationFrame(rafId);
    }, [showModal, showModals, subSelectedCard]);

    // Keep sidebar selection aligned with the open chat panel (survives history refresh)
    useEffect(() => {
        if (!showModal || showModals.length === 0) {
            return;
        }
        const active = showModals[0];
        if (active?.capabilityId == null) {
            return;
        }
        const capId = active.capabilityId;
        const threadSubId = chatApiSubCapabilityId(active.subCapabilityId);
        setSelectedCard(capId);
        setSubSelectedCard(threadSubId);
        setExpandedCapability(capId);
    }, [showModal, showModals]);

    useEffect(() => {
        if (!showModal || subSelectedCard == null || selectedCard == null || !userId || !projectId) {
            return;
        }

        const latestSmeMessage = chatHistory
            .filter(
                (chat) =>
                    chat.senderName === "SME" &&
                    sameAssessmentUnit(
                        chat.capabilityId,
                        chat.subCapabilityId,
                        selectedCard,
                        subSelectedCard
                    )
            )
            .sort((a, b) => new Date(b.messageDate) - new Date(a.messageDate))[0];

        if (latestSmeMessage?.messageDate) {
            markChatThreadViewed(
                userId,
                projectId,
                selectedCard,
                subSelectedCard,
                latestSmeMessage.messageDate
            );
            setReadStateVersion((version) => version + 1);
        }
    }, [showModal, selectedCard, subSelectedCard, chatHistory, userId, projectId]);

    // Show New badge briefly, then auto-open the unread chat after 5 seconds
    useEffect(() => {
        if (
            !latestMessage ||
            !userId ||
            !projectId ||
            !data?.data?.selectedCapabilities ||
            userOpenedChatRef.current
        ) {
            return;
        }

        if (
            !isNotificationUnread(
                userId,
                projectId,
                latestMessage.capabilityId,
                latestMessage.subCapabilityId,
                latestMessage.messageDate
            )
        ) {
            return;
        }

        if (autoOpenTimerRef.current) {
            clearTimeout(autoOpenTimerRef.current);
        }

        autoOpenTimerRef.current = setTimeout(() => {
            if (userOpenedChatRef.current) {
                return;
            }

            const subCapabilityName = data.data.selectedCapabilities
                .flatMap((cap) => cap.selectedSubCapabilities)
                .find((subCap) =>
                    sameAssessmentUnit(
                        latestMessage.capabilityId,
                        latestMessage.subCapabilityId,
                        latestMessage.capabilityId,
                        subCap.subCapabilityId
                    )
                )
                ?.subCapability;

            setExpandedCapability(latestMessage.capabilityId);
            setSelectedCard(latestMessage.capabilityId);
            setSubSelectedCard(latestMessage.subCapabilityId);
            setShowModals([
                {
                    capabilityId: latestMessage.capabilityId,
                    subCapabilityId: latestMessage.subCapabilityId,
                    subCapability: subCapabilityName,
                },
            ]);
            setShowModal(true);
        }, 5000);

        return () => {
            if (autoOpenTimerRef.current) {
                clearTimeout(autoOpenTimerRef.current);
                autoOpenTimerRef.current = null;
            }
        };
    }, [latestMessage, userId, projectId, data, body]);

    useEffect(() => {
        return () => {
            body.classList.remove("modal-open");
        };
    }, [body]);

    const handleFileChange = (subCapabilityId, e) => {
      const selectedFile = e.target.files[0];
      if (selectedFile) {
          setFilesBySubCapability(prev => ({ ...prev, [subCapabilityId]: selectedFile }));
      }
    };

    const sendMessage = async (capabilityId, subCapabilityId) => {
        const storageKey = chatMessageStorageKey(capabilityId, subCapabilityId);
        try {
            if (!messages[storageKey] || messages[storageKey].trim() === "") {
                showAlert("Please enter a response before sending.", "warning");
                return;
            }
    
            setIsDisabled(true);
    
            await handleSendMessage(
                userId,
                capabilityId,
                subCapabilityId,
                projectId,
                messages[storageKey] || "",
                filesBySubCapability[storageKey],
                null,
                null,
                null,
                userName
            );
    
            setMessages((prev) => ({ ...prev, [storageKey]: "" }));
            setFilesBySubCapability((prev) => ({ ...prev, [storageKey]: null }));
            setLatestMessage(null);
            fetchChatHistory();
    
        } catch (error) {
            console.error("Error sending message:", error.message);
            showAlert(error?.message || "Failed to send message. Please try again.", "error");
        } finally {
            setIsDisabled(false);
        }
    };

    const handleMessageKeyDown = (event, capabilityId, subCapabilityId) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            if (!isDisabled) {
                sendMessage(capabilityId, subCapabilityId);
            }
        }
    };

    const handleDownload = async (filename) => {
        try {
            await downloadChatFile(filename);
        } catch (error) {
            console.error("Error downloading file:", error.message);
        }
    };

    const isInputDisabled = (capabilityId, subCapabilityId) => {
        const relevantChats = chatHistory.filter((chat) =>
            sameAssessmentUnit(
                chat.capabilityId,
                chat.subCapabilityId,
                capabilityId,
                subCapabilityId
            )
        );
        if (relevantChats.length === 0) return true;
        const lastMessage = relevantChats[relevantChats.length - 1];
        return lastMessage.senderName !== 'SME';
    };

    const getFileIcon = (filename) => {
        const extension = filename.split(".").pop().toLowerCase();
        switch (extension) {
            case "pdf":
                return basename+"/images/pdf.webp";
            case "docx":
                return basename+"/images/docx.webp";
            case "png":
                return basename+"/images/png-file-.webp";
            case "jpg":
                return basename+"/images/jpg.webp";
            default:
                return basename+"/images/file.webp";
        }
    };

    const handleCapabilityInteraction = (capabilityId, subCapabilityId = null, subCapabilityName = null) => {
        if (subCapabilityId == null) {
            setExpandedCapability((prev) => (prev === capabilityId ? null : capabilityId));
            setSelectedCard((prev) => (prev === capabilityId ? null : capabilityId));
            setSubSelectedCard(null);
            setShowModals([]);
            setShowModal(false);
            return;
        }

        userOpenedChatRef.current = true;
        if (autoOpenTimerRef.current) {
            clearTimeout(autoOpenTimerRef.current);
            autoOpenTimerRef.current = null;
        }
    
        const modalsToShow = [];
        const seenSubCapabilities = new Set();
    
        const threadSubId = chatApiSubCapabilityId(subCapabilityId);

        chatHistory
            .filter((chat) =>
                sameAssessmentUnit(
                    chat.capabilityId,
                    chat.subCapabilityId,
                    capabilityId,
                    threadSubId
                )
            )
            .forEach((chat) => {
                const threadKey = chatMessageStorageKey(capabilityId, chat.subCapabilityId);
                if (!seenSubCapabilities.has(threadKey)) {
                    seenSubCapabilities.add(threadKey);
                    modalsToShow.push({
                        capabilityId,
                        subCapabilityId: chatApiSubCapabilityId(chat.subCapabilityId),
                        subCapability: subCapabilityName,
                        latestSMEChat: chat,
                    });
                }
            });

        if (modalsToShow.length === 0) {
            modalsToShow.push({
                capabilityId,
                subCapabilityId: threadSubId,
                subCapability: subCapabilityName,
            });
        }
    
        setShowModals(modalsToShow);
        setSelectedCard(capabilityId);
        setSubSelectedCard(threadSubId);
        setSelectedSubCapability({
            capabilityId,
            subCapabilityId: threadSubId,
            subCapability: subCapabilityName,
        });
    
        setShowModal(true);
    };      

    const formatCustomDate = (date) => {
        const options = {
            weekday: 'long',
            month: 'short',
            day: '2-digit',
            year: 'numeric',
            //hour: '2-digit',
            // minute: '2-digit',
            // second: '2-digit',
            // timeZone: 'America/New_York', // Specify the US time zone (e.g., Eastern Time)
            // timeZoneName: 'short' // Optional: Adds timezone abbreviation (e.g., EST)
        };
        return new Intl.DateTimeFormat('en-US', options).format(date);
    };    

    const formatChatDate = (date) => {
        const options = { month: 'short', day: '2-digit', year: 'numeric' }; // Customize the date part
        const datePart = new Intl.DateTimeFormat('en-US', options).format(date); // Format like "Oct 02 2024"
        
        const timePart = date.toLocaleTimeString('en-US'); // Extract time in 12-hour format with AM/PM
        return `${datePart}, ${timePart}`;
      };

    useEffect(() => {
        if (chatHistory.length > 0) {
            const lastSMEMessage = chatHistory.find(chat => chat.senderName === 'SME');
            if (lastSMEMessage) {
                setIsDisabled(false);
            } else {
                setIsDisabled(true);
            }
        }
    }, [chatHistory]);

    const openModal = async (modal) => {
        setSelectedSubCapability(modal);
        await fetchAssessmentData(modal);
        setShowAssessmentModal(true);
    };

    const closeModal = () => {
        setShowAssessmentModal(false);
    };

    const formatMessageTime = (value) => {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return '';
        }
        const day = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
        const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        return `${day}, ${time}`;
    };

    // Only capabilities the SME has actually started a thread for are listed.
    const visibleCapabilities = (data?.data?.selectedCapabilities || []).filter((capability) =>
        capability.selectedSubCapabilities.some((subCapability) =>
            chatHistory.some((chat) =>
                chatMatchesAssessmentUnit(chat, capability.capabilityId, subCapability.subCapabilityId, { smeOnly: true })
            )
        )
    );

    const activeModal = showModals[0] ?? null;
    const activeThreadKey = activeModal
        ? chatMessageStorageKey(activeModal.capabilityId, activeModal.subCapabilityId)
        : '';
    // Oldest first, like a normal conversation; the view scrolls to the latest message.
    const activeThreadMessages = activeModal
        ? getThreadMessagesNewestFirst(chatHistory, activeModal.capabilityId, activeModal.subCapabilityId)
              .slice()
              .reverse()
        : [];

    useEffect(() => {
        const el = threadBodyRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [activeThreadKey, activeThreadMessages.length]);

    const viewAssessmentUnit =
        showAssessmentModal &&
        selectedSubCapability &&
        typeof selectedSubCapability === 'object'
            ? selectedSubCapability
            : null;

    return (
          <>
            {walkthrough}
            <ClientAppShell
                title="Chat"
                dataNav="chat"
                intro={{
                    eyebrow: 'Advisor collaboration',
                    description:
                        'Message your assigned subject matter advisor, follow threads by capability, and keep full chat history for context as your assessment progresses.',
                }}
            >
                <div className="dash-frame-wrapper">
                  <div className="dash-frame-div">
                <div className="chat-toolbar-section">
                            <div className="chat-download-toolbar">
                                <label htmlFor="capability-filter" className="chat-filter-label">
                                    Download Chat
                                </label>
                                <select
                                    className="chat-filter-select form-select"
                                    id="capability-filter"
                                    value={selectedCapability}
                                    onChange={(e) => {
                                        setSelectedCapability(e.target.value);
                                        setSelectedSubCapability("all");
                                    }}
                                >
                                <option value="all">All Capabilities</option>
                                {getFilteredCapabilities().map((capability) => (
                                    <option key={capability.capabilityId} value={capability.capabilityId}>
                                        {capability.capability}
                                    </option>
                                ))}
                            </select>
                                {selectedCapability !== "all" && (
                                <>
                                    <select
                                        className="chat-filter-select form-select"
                                        id="sub-capability-filter"
                                        value={selectedSubCapability}
                                        onChange={(e) => setSelectedSubCapability(e.target.value)}
                                    >
                                        <option value="all">All Sub-Capabilities</option>
                                        {getFilteredSubCapabilities().map((subCapability) => (
                                            <option key={subCapability.subCapabilityId} value={subCapability.subCapabilityId}>
                                                {subCapability.subCapability}
                                            </option>
                                        ))}
                                    </select>
                                </>
                            )}

                                <button
                                    type="button"
                                    onClick={handleDownloadChat}
                                    className="btn-dashboard-primary chat-download-btn"
                                >
                                    Download
                                </button>
                            </div>
                </div>
                <div className="client-smechat-wrap">
                  <div className="smechat">
                    <nav className="smechat-nav" aria-label="Conversations">
                      <div className="smechat-nav__head">
                        <p className="smechat-nav__label">Conversations</p>
                        {visibleCapabilities.length > 0 ? (
                          <span className="smechat-nav__count">{visibleCapabilities.length}</span>
                        ) : null}
                      </div>
                      <div className="smechat-nav__list">
                        {loading ? (
                          <ContentLoader compact label="Loading chats…" />
                        ) : visibleCapabilities.length === 0 ? (
                          <p className="smechat-nav__empty">
                            {!data || !data.data || !data.data.selectedCapabilities?.length
                              ? "No chats available yet."
                              : "No advisor conversations yet. Threads appear here once your SME starts a chat."}
                          </p>
                        ) : (
                          visibleCapabilities.map((capability) => {
                            const leaf = isLeafSelectedCapability(capability);
                            const firstUnit = capability.selectedSubCapabilities?.[0];
                            const activeUnitKey =
                              selectedCard === capability.capabilityId && subSelectedCard != null
                                ? assessmentUnitKey(capability.capabilityId, subSelectedCard)
                                : null;
                            const isThreadActive = isCapabilityNavHighlighted(capability.capabilityId, {
                              leaf,
                              firstSubCapabilityId: firstUnit?.subCapabilityId,
                              selectedCard,
                              subSelectedCard: activeUnitKey,
                              activeUnitKey,
                            });
                            const hasUnread =
                              readStateVersion >= 0 &&
                              hasUnreadForCapability(unreadThreads, capability.capabilityId);
                            const isExpanded = !leaf && expandedCapability === capability.capabilityId;
                            const threadSubs = capability.selectedSubCapabilities.filter((subCapability) =>
                              chatHistory.some((chat) =>
                                chatMatchesAssessmentUnit(
                                  chat,
                                  capability.capabilityId,
                                  subCapability.subCapabilityId,
                                  { smeOnly: true }
                                )
                              )
                            );
                            return (
                              <div key={capability.capabilityId} className="smechat-nav__group">
                                <button
                                  type="button"
                                  className={`smechat-nav__item${isThreadActive ? " is-active" : ""}`}
                                  aria-expanded={leaf ? undefined : isExpanded}
                                  onClick={() => {
                                    if (leaf && firstUnit) {
                                      handleCapabilityInteraction(
                                        capability.capabilityId,
                                        chatApiSubCapabilityId(firstUnit.subCapabilityId),
                                        firstUnit.subCapability || capability.capability
                                      );
                                      return;
                                    }
                                    handleCapabilityInteraction(capability.capabilityId);
                                  }}
                                >
                                  <span className={`smechat-nav__dot${hasUnread ? " is-unread" : ""}`} aria-hidden="true" />
                                  <span className="smechat-nav__text">
                                    <span className="smechat-nav__title">{capability.capability}</span>
                                    {!leaf ? (
                                      <span className="smechat-nav__sub">
                                        {threadSubs.length}/{capability.selectedSubCapabilities.length} sub-capabilities
                                      </span>
                                    ) : null}
                                  </span>
                                  {hasUnread ? (
                                    <span className="smechat-nav__badge" aria-label="New message">New</span>
                                  ) : null}
                                  {!leaf ? (
                                    <span className={`smechat-nav__chev${isExpanded ? " is-open" : ""}`} aria-hidden="true" />
                                  ) : null}
                                </button>
                                {isExpanded ? (
                                  <div
                                    ref={(el) => (dropdownRefs.current[capability.capabilityId] = el)}
                                    className="smechat-nav__subs"
                                  >
                                    {threadSubs.map((subCapability) => {
                                      const isSubActive =
                                        selectedCard === capability.capabilityId &&
                                        sameAssessmentUnit(
                                          capability.capabilityId,
                                          subCapability.subCapabilityId,
                                          capability.capabilityId,
                                          subSelectedCard
                                        );
                                      const subUnread =
                                        readStateVersion >= 0 &&
                                        hasUnreadForSubCapability(
                                          unreadThreads,
                                          capability.capabilityId,
                                          subCapability.subCapabilityId
                                        );
                                      return (
                                        <button
                                          type="button"
                                          key={assessmentUnitKey(capability.capabilityId, subCapability.subCapabilityId)}
                                          className={`smechat-nav__sub-item${isSubActive ? " is-active" : ""}`}
                                          onClick={() =>
                                            handleCapabilityInteraction(
                                              capability.capabilityId,
                                              chatApiSubCapabilityId(subCapability.subCapabilityId),
                                              subCapability.subCapability
                                            )
                                          }
                                        >
                                          <span className={`smechat-nav__dot${subUnread ? " is-unread" : ""}`} aria-hidden="true" />
                                          <span className="smechat-nav__sub-label">{subCapability.subCapability}</span>
                                          {subUnread ? (
                                            <span className="smechat-nav__badge" aria-label="New message">New</span>
                                          ) : null}
                                        </button>
                                      );
                                    })}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </nav>

                    <section className="smechat-main" ref={messagePanelRef}>
                      {!showModal || !activeModal ? (
                        <div className="smechat-empty" role="status">
                          {loading ? (
                            <ContentLoader compact label="Loading conversation…" />
                          ) : (
                            <>
                              <span className="smechat-empty__icon" aria-hidden="true" />
                              <p className="smechat-empty__title">
                                {visibleCapabilities.length === 0 ? "No conversations yet" : "Select a conversation"}
                              </p>
                              <p className="smechat-empty__text">
                                {visibleCapabilities.length === 0
                                  ? "Chats with your subject matter advisor will appear here once they begin an assessment thread."
                                  : "Choose a capability thread on the left to view messages and reply to your advisor."}
                              </p>
                            </>
                          )}
                        </div>
                      ) : (
                        <>
                          <header className="smechat-main__head">
                            <h2 className="smechat-main__title">{activeModal.subCapability}</h2>
                            <div className="smechat-main__actions">
                              <a
                                href="#"
                                className="smechat-main__link"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openModal(activeModal);
                                }}
                              >
                                View assessment
                              </a>
                            </div>
                          </header>

                          <div className="smechat-thread" ref={threadBodyRef}>
                            {activeThreadMessages.length === 0 ? (
                              <p className="smechat-thread__empty">No messages yet in this conversation.</p>
                            ) : null}
                            {activeThreadMessages.map((chat, index) => {
                              const fromAdvisor = chat.senderName === "SME";
                              return (
                                <article
                                  key={chat.id || index}
                                  // smechat styles "sme" as the right-hand/own side; here the client is "me"
                                  className={`smechat-msg ${fromAdvisor ? "smechat-msg--client" : "smechat-msg--sme"}`}
                                >
                                  <div className="smechat-msg__meta">
                                    <span className="smechat-msg__sender">{fromAdvisor ? "SME" : "You"}</span>
                                    <time className="smechat-msg__time">{formatMessageTime(chat.messageDate)}</time>
                                  </div>
                                  <div className="smechat-msg__bubble">{chat.message}</div>
                                  {chat.attachments && chat.attachments.length > 0 ? (
                                    <div className="smechat-msg__files">
                                      {chat.attachments.map((attachment, fileIndex) => (
                                        <button
                                          type="button"
                                          key={fileIndex}
                                          className="smechat-file"
                                          onClick={() => handleDownload(attachment.attachmentUrl.split('/').pop())}
                                        >
                                          <img src={getFileIcon(attachment.attachmentUrl)} alt="" className="smechat-file__icon" />
                                          <span className="smechat-file__name">{attachment.attachmentUrl.split('/').pop()}</span>
                                        </button>
                                      ))}
                                    </div>
                                  ) : null}
                                </article>
                              );
                            })}
                          </div>

                          <div className="smechat-composer" data-element="message-input">
                            <div className="smechat-composer__box">
                              <textarea
                                className="smechat-composer__input"
                                placeholder="Your response"
                                rows={2}
                                value={messages[activeThreadKey] || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setMessages((prev) => ({ ...prev, [activeThreadKey]: value }));
                                }}
                                onKeyDown={(e) =>
                                  handleMessageKeyDown(e, activeModal.capabilityId, activeModal.subCapabilityId)
                                }
                                disabled={isDisabled}
                              />
                              {filesBySubCapability[activeThreadKey] ? (
                                <div className="smechat-composer__attached">
                                  <span>Attached: {filesBySubCapability[activeThreadKey].name}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFilesBySubCapability((prev) => ({ ...prev, [activeThreadKey]: null }));
                                      const input = document.getElementById(`file-upload-${activeThreadKey}`) as HTMLInputElement | null;
                                      if (input) input.value = '';
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ) : null}
                              <div className="smechat-composer__bar">
                                <div className="smechat-composer__tools">
                                  <button
                                    type="button"
                                    className="smechat-icon-btn"
                                    aria-label="Voice input"
                                    onClick={() => toggleSpeechRecognition(activeThreadKey)}
                                  >
                                    <img
                                      src={`${basename}/images/mic.svg`}
                                      alt=""
                                      className={isListening === activeThreadKey ? "is-listening" : ""}
                                    />
                                  </button>
                                  <input
                                    type="file"
                                    className="smechat-composer__file"
                                    onChange={(e) => handleFileChange(activeThreadKey, e)}
                                    id={`file-upload-${activeThreadKey}`}
                                  />
                                  <label
                                    htmlFor={`file-upload-${activeThreadKey}`}
                                    className="smechat-icon-btn"
                                    aria-label="Attach file"
                                  >
                                    <img src={`${basename}/images/Vector.svg`} alt="" />
                                  </label>
                                </div>
                                <button
                                  type="button"
                                  className="smechat-send"
                                  onClick={() => sendMessage(activeModal.capabilityId, activeModal.subCapabilityId)}
                                  disabled={isDisabled}
                                >
                                  Send
                                </button>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </section>
                  </div>
                                    <Modal
                                        open={Boolean(viewAssessmentUnit)}
                                        onClose={closeModal}
                                        size="2xl"
                                        elevated
                                        className="chat-view-assessment-modal"
                                        title={
                                            viewAssessmentUnit?.subCapability
                                                ? `Sub capability: ${viewAssessmentUnit.subCapability}`
                                                : 'View assessment'
                                        }
                                    >
                                        <div className="chat-view-assessment-modal__grid">
                                            <section className="chat-view-assessment-modal__column" aria-labelledby="chat-view-current-heading">
                                                <header className="chat-view-assessment-modal__section-head">
                                                    <h3
                                                        id="chat-view-current-heading"
                                                        className="chat-view-assessment-modal__section-title required"
                                                    >
                                                        Current state
                                                    </h3>
                                                    <p className="chat-view-assessment-modal__section-desc">
                                                        Describe the current state of your organization as you perceive it.
                                                    </p>
                                                </header>
                                                <div className="chat-view-assessment-modal__radios" role="radiogroup" aria-labelledby="chat-view-current-heading">
                                                    {CHAT_MATURITY_STATES.map((state) => (
                                                        <div key={state} className="chat-view-assessment-modal__radio-row">
                                                            <input
                                                                type="radio"
                                                                id={`chat-view-current-${state}`}
                                                                name="chatViewCurrentState"
                                                                value={state}
                                                                checked={currentState === state}
                                                                disabled
                                                            />
                                                            <label htmlFor={`chat-view-current-${state}`}>
                                                                {formatMaturityLabel(state)}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                                <textarea
                                                    className="chat-view-assessment-modal__textarea"
                                                    placeholder="Current state comment"
                                                    value={currentStatusDescription}
                                                    disabled
                                                    rows={6}
                                                />
                                            </section>
                                            <div className="chat-view-assessment-modal__divider" aria-hidden="true" />
                                            <section className="chat-view-assessment-modal__column" aria-labelledby="chat-view-future-heading">
                                                <header className="chat-view-assessment-modal__section-head">
                                                    <h3
                                                        id="chat-view-future-heading"
                                                        className="chat-view-assessment-modal__section-title required"
                                                    >
                                                        Future state
                                                    </h3>
                                                    <p className="chat-view-assessment-modal__section-desc">
                                                        Describe the future state you envision for your organization.
                                                    </p>
                                                </header>
                                                <div className="chat-view-assessment-modal__radios" role="radiogroup" aria-labelledby="chat-view-future-heading">
                                                    {CHAT_MATURITY_STATES.map((state) => (
                                                        <div key={state} className="chat-view-assessment-modal__radio-row">
                                                            <input
                                                                type="radio"
                                                                id={`chat-view-future-${state}`}
                                                                name="chatViewFutureState"
                                                                value={state}
                                                                checked={futureState === state}
                                                                disabled
                                                            />
                                                            <label htmlFor={`chat-view-future-${state}`}>
                                                                {formatMaturityLabel(state)}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                                <textarea
                                                    className="chat-view-assessment-modal__textarea"
                                                    placeholder="Future state comment"
                                                    value={futureStatusDescription}
                                                    disabled
                                                    rows={6}
                                                />
                                            </section>
                                        </div>
                                    </Modal>
                </div>
                  </div>
                </div>
            </ClientAppShell>
        </>
    );
};
export default Chat;
